import { forwardRef, type HTMLAttributes } from 'react';

import { useMaskLink } from '@tmh-website/services/link.service';
import { LinkField, NavigationHighlightLinkGroup } from '@tmh-website/shared/cms';
import { LocalePrefix } from '@tmh-website/shared/const';

import * as Styled from '../NavigationLink/NavigationLink.styles';

interface NavigationLinkProps extends HTMLAttributes<HTMLAnchorElement> {
  locale: LocalePrefix;
  link: LinkField;
  disabled?: boolean;
  /**
   * Mask the link using base64 encoding. This is used for SEO optimization
   */
  maskLink?: boolean;
  visibleHighlightLinkGroups: NavigationHighlightLinkGroup[];
}

const NavigationLinkStandalone = forwardRef<HTMLAnchorElement, NavigationLinkProps>(
  ({ locale, link, disabled = false, maskLink = false, visibleHighlightLinkGroups, ...props }, ref) => {
    let href = link.value;

    // Add locale prefix to href when not using the NextJS router
    if (href.startsWith('/')) {
      href = `/${locale}${href}`;
    }

    const { renderedHref, onMouseEnter, onMouseLeave } = useMaskLink(maskLink, href);

    if (href) {
      return (
        <Styled.ClickArea
          {...props}
          href={renderedHref}
          as={renderedHref ? 'a' : 'button'}
          onMouseEnter={e => {
            if (onMouseEnter) onMouseEnter();
            if (props.onMouseEnter) props.onMouseEnter(e);
          }}
          onMouseLeave={e => {
            if (onMouseLeave) onMouseLeave();
            if (props.onMouseLeave) props.onMouseLeave(e);
          }}
          style={{ display: disabled ? 'none' : undefined }}
          ref={ref}>
          <Styled.NavLink aria-disabled={disabled ? 'true' : undefined}>{link.label}</Styled.NavLink>
        </Styled.ClickArea>
      );
    } else return null;
  }
);

NavigationLinkStandalone.displayName = 'NavigationLinkStandalone';

export default NavigationLinkStandalone;
